import { useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import {
  AirVent,
  Wifi,
  Tv,
  ShowerHead,
  Flame,
  Wine,
  TreePine,
  Building2,
  Sofa,
  Users,
  Maximize,
  BedDouble,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { rooms as staticRooms, type Room } from '@/data/rooms'
import { mergeBackendRooms } from '@/data/rooms-merge'
import { useAvailabilityQuery, useHotelProfile } from '../api/hooks'
import { BOOKING_ROUTE, SITE_NAME } from '@/lib/constants'
import { bookingStateForRoom } from '@/lib/booking-state'
import { getImageUrl } from '@/lib/image-url'
import { BookingDateGuestPicker } from '@/components/BookingDateGuestPicker'
import type { SearchState } from '@/api/types'

function todayISO(): string {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function tomorrowISO(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function readSearchFromQuery(qs: string): SearchState {
  const params = new URLSearchParams(qs)
  const check_in = params.get('check_in') || todayISO()
  const check_out = params.get('check_out') || tomorrowISO()
  const adults = Math.max(1, Number(params.get('adults') ?? 2) || 2)
  const children = Math.max(0, Number(params.get('children') ?? 0) || 0)
  return { check_in, check_out, adults, children }
}

const amenityIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  ac: AirVent,
  aire_acondicionado: AirVent,
  wifi: Wifi,
  tv: Tv,
  television: Tv,
  private_bathroom: ShowerHead,
  bano_privado: ShowerHead,
  hot_water: Flame,
  agua_caliente: Flame,
  minibar: Wine,
  garden_view: TreePine,
  vista_jardin: TreePine,
  balcony: Building2,
  balcon: Building2,
  sitting_area: Sofa,
}

const amenityLabels = {
  es: {
    ac: 'Aire Acondicionado',
    aire_acondicionado: 'Aire Acondicionado',
    wifi: 'WiFi Gratis',
    tv: 'TV por Cable',
    television: 'TV por Cable',
    private_bathroom: 'Baño Privado',
    bano_privado: 'Baño Privado',
    hot_water: 'Agua Caliente',
    agua_caliente: 'Agua Caliente',
    minibar: 'Minibar',
    garden_view: 'Vista al Jardín',
    vista_jardin: 'Vista al Jardín',
    balcony: 'Balcón',
    balcon: 'Balcón',
    sitting_area: 'Sala de Estar',
  } as Record<string, string>,
  en: {
    ac: 'Air Conditioning',
    aire_acondicionado: 'Air Conditioning',
    wifi: 'Free WiFi',
    tv: 'Cable TV',
    television: 'Cable TV',
    private_bathroom: 'Private Bathroom',
    bano_privado: 'Private Bathroom',
    hot_water: 'Hot Water',
    agua_caliente: 'Hot Water',
    minibar: 'Minibar',
    garden_view: 'Garden View',
    vista_jardin: 'Garden View',
    balcony: 'Balcony',
    balcon: 'Balcony',
    sitting_area: 'Sitting Area',
  } as Record<string, string>,
}

export function RoomDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: hotel } = useHotelProfile()
  const merged = mergeBackendRooms(hotel?.featured_room_types)
  const room =
    merged.find((r) => r.slug === slug) ?? staticRooms.find((r) => r.slug === slug)
  if (!room) return <Navigate to="/rooms" replace />
  return <RoomDetailContent room={room} />
}

function RoomDetailContent({ room }: { room: Room }) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language.startsWith('en') ? 'en' : 'es'
  const navigate = useNavigate()
  const location = useLocation()
  const [currentImage, setCurrentImage] = useState(0)
  const labels = amenityLabels[locale]

  const [search, setSearch] = useState<SearchState>(() => readSearchFromQuery(location.search))

  const availabilityQuery = useAvailabilityQuery(search)
  const matchedAvailability = useMemo(
    () => availabilityQuery.data?.find((r) => r.room_type_id === room.room_type_id) ?? null,
    [availabilityQuery.data, room.room_type_id],
  )

  const nights = useMemo(() => {
    const a = new Date(search.check_in)
    const b = new Date(search.check_out)
    const diff = Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000))
    return Number.isFinite(diff) && diff > 0 ? diff : 0
  }, [search.check_in, search.check_out])

  const handleBook = () => {
    const state = bookingStateForRoom(room, search, matchedAvailability)
    if (state) navigate(BOOKING_ROUTE, { state })
    else navigate('/contact')
  }

  const formatHNL = (value: string | number | undefined) => {
    if (value == null) return null
    const num = Math.round(Number(value))
    if (!Number.isFinite(num)) return null
    return `L.${num.toLocaleString()}`
  }

  const prevImage = () =>
    setCurrentImage((prev) => (prev === 0 ? room.images.length - 1 : prev - 1))
  const nextImage = () =>
    setCurrentImage((prev) => (prev === room.images.length - 1 ? 0 : prev + 1))

  const name = t(`rooms.types.${room.translationKey}.name`)
  const description = t(`rooms.types.${room.translationKey}.description`)

  return (
    <>
      <Helmet>
        <title>{`${name} | ${SITE_NAME}`}</title>
        <meta name="description" content={description as string} />
      </Helmet>
      <section className="bg-cream-light pt-24 pb-12 md:pt-32 md:pb-20">
        <div className="mx-auto max-w-7xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Link
              to="/rooms"
              className="mb-6 inline-flex items-center text-sm text-terracotta hover:underline md:mb-8"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              {t('rooms.view_all')}
            </Link>

            <div className="grid gap-6 md:gap-8 lg:grid-cols-2 lg:gap-12">
              <div className="min-w-0">
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg">
                  <img
                    src={getImageUrl(room.images[currentImage])}
                    alt={name}
                    className="h-full w-full object-cover"
                  />
                  {room.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-wood-dark shadow-md transition hover:bg-white"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-wood-dark shadow-md transition hover:bg-white"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
                {room.images.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto">
                    {room.images.map((img, idx) => (
                      <button
                        key={img}
                        onClick={() => setCurrentImage(idx)}
                        className={`relative aspect-[4/3] w-20 flex-shrink-0 overflow-hidden rounded-md border-2 transition ${
                          currentImage === idx
                            ? 'border-terracotta'
                            : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={getImageUrl(img)}
                          alt={`${name} ${idx + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <Badge variant="gold" className="mb-4">
                  {room.module === 'colonial'
                    ? locale === 'es'
                      ? 'Módulo Colonial'
                      : 'Colonial Wing'
                    : locale === 'es'
                      ? 'Módulo Moderno'
                      : 'Modern Wing'}
                </Badge>

                <h1 className="mb-4 font-heading text-2xl font-bold text-wood-dark sm:text-3xl md:text-4xl">
                  {name}
                </h1>

                <p className="mb-6 text-base leading-relaxed text-stone sm:text-lg">{description}</p>

                <div className="mb-8 flex flex-wrap gap-4">
                  <span className="inline-flex items-center gap-2 text-sm text-stone">
                    <Users className="h-4 w-4 text-terracotta" />
                    {room.capacity} {t('rooms.guests')}
                  </span>
                  {room.size !== '—' && (
                    <span className="inline-flex items-center gap-2 text-sm text-stone">
                      <Maximize className="h-4 w-4 text-terracotta" />
                      {room.size}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2 text-sm text-stone">
                    <BedDouble className="h-4 w-4 text-terracotta" />
                    {room.bed}
                  </span>
                </div>

                <div className="mb-6 rounded-lg bg-white p-4 shadow-sm sm:p-6 md:mb-8">
                  <BookingDateGuestPicker
                    value={search}
                    onChange={setSearch}
                    maxOccupancy={matchedAvailability?.max_occupancy ?? room.capacity}
                  />
                  {nights > 0 && (
                    <div className="mt-4 border-t border-cream-dark pt-4 text-sm">
                      {availabilityQuery.isPending && (
                        <p className="text-stone">Calculando precio…</p>
                      )}
                      {!availabilityQuery.isPending && !matchedAvailability && (
                        <p className="text-terracotta">
                          No hay disponibilidad de {t(`rooms.types.${room.translationKey}.name`)}{' '}
                          para esas fechas con esa cantidad de huéspedes.
                        </p>
                      )}
                      {matchedAvailability && (
                        <>
                          <div className="flex items-baseline justify-between">
                            <span className="text-stone">
                              Tarifa por noche ({nights} {nights === 1 ? 'noche' : 'noches'})
                            </span>
                            <span className="font-semibold text-wood-dark">
                              {formatHNL(matchedAvailability.estimated_nightly_rate)}
                            </span>
                          </div>
                          {matchedAvailability.extra_person &&
                            matchedAvailability.extra_person.count > 0 && (
                              <div className="mt-1 flex items-baseline justify-between text-stone">
                                <span>
                                  Persona adicional ({matchedAvailability.extra_person.count} ×{' '}
                                  {matchedAvailability.extra_person.nights}{' '}
                                  {matchedAvailability.extra_person.nights === 1 ? 'noche' : 'noches'})
                                </span>
                                <span>
                                  {formatHNL(matchedAvailability.extra_person.amount_with_tax)}
                                </span>
                              </div>
                            )}
                          <div className="mt-3 flex items-baseline justify-between border-t border-cream-dark pt-3">
                            <span className="text-stone">Total estimado</span>
                            <span className="text-2xl font-bold text-terracotta">
                              {formatHNL(matchedAvailability.estimated_total)}
                            </span>
                          </div>
                          <p className="mt-1 text-[11px] uppercase tracking-wide text-gold">
                            {t('rooms.taxes_included')}
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <h3 className="mb-4 font-heading text-xl font-semibold text-wood-dark">
                  {locale === 'es' ? 'Amenidades' : 'Amenities'}
                </h3>
                <ul className="mb-8 grid grid-cols-2 gap-3">
                  {room.amenities.map((a) => {
                    const Icon = amenityIcons[a]
                    return (
                      <li
                        key={a}
                        className="flex items-center gap-2 text-sm text-stone"
                      >
                        {Icon && <Icon className="h-4 w-4 text-terracotta" />}
                        {labels[a] || a}
                      </li>
                    )
                  })}
                </ul>

                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleBook}
                  disabled={
                    nights <= 0 ||
                    availabilityQuery.isPending ||
                    matchedAvailability == null ||
                    (matchedAvailability.available_rooms_count ?? 0) <= 0
                  }
                >
                  {t('rooms.book_room')}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
